import React from 'react';
import { Link } from '@tanstack/react-router';
import { User, MapPin, Calendar, Route, Users } from 'lucide-react';
import { Badge, Avatar, AvatarFallback, AvatarImage } from '@city-sage/aurum-avis-labs-components';
import { cn } from '@city-sage/aurum-avis-labs-components';
import { Account } from '@city-sage/core-client';
import { useFormattedDate } from '../../lib/dateUtils';

interface UserSearchCardProps {
  account: Account;
  searchQuery: string;
  className?: string;
  onClose?: () => void;
}

export function UserSearchCard({ 
  account, 
  searchQuery, 
  className,
  onClose
}: UserSearchCardProps) {
  const { formatDate } = useFormattedDate();
  
  const highlightText = (text: string, query: string) => {
    if (!query) return text;
    
    const regex = new RegExp(`(${query})`, 'gi');
    const parts = text.split(regex);
    
    return parts.map((part, index) => 
      regex.test(part) ? (
        <mark key={index} className="bg-yellow-200 text-black font-medium">
          {part}
        </mark>
      ) : (
        part
      )
    );
  };

  const getDisplayName = () => {
    return account.userName || account.email?.split('@')[0] || 'Unknown User';
  };

  const getJoinDate = () => {
    if (account.createdAt) {
      return formatDate(account.createdAt, { 
        year: 'numeric', 
        month: 'short' 
      });
    }
    return null;
  };

  const getInitials = () => {
    const displayName = getDisplayName();
    if (displayName.includes(' ')) {
      const parts = displayName.split(' ');
      return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
    }
    return displayName.slice(0, 2).toUpperCase();
  };

  return (
    <Link to="/users/$userId" params={{ userId: account.id }} onClick={onClose}>
      <div className={cn(
        "bg-white px-6 py-4 hover:bg-gray-50 transition-colors duration-200 border-b border-gray-100 last:border-b-0",
        className
      )}>
        <div className="flex gap-4">
          {/* Avatar */}
          <div className="relative flex-shrink-0">
            <Avatar className="h-12 w-12">
              <AvatarImage 
                src={account.avatarUrl} 
                alt={getDisplayName()}
              />
              <AvatarFallback className="bg-gradient-to-br from-purple-100 to-blue-100 text-purple-600 font-medium">
                {getInitials()}
              </AvatarFallback>
            </Avatar>
          </div>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-2">
            {/* Name & Handle */}
            <div>
              <div className="flex items-center gap-2">
                <h3 className="font-semibold text-base text-gray-900 truncate">
                  {highlightText(getDisplayName(), searchQuery)}
                </h3>
              </div>
              
            </div>

            {/* About Me */}
            {account.aboutMe && (
              <p className="text-sm text-gray-600 line-clamp-2">
                {highlightText(account.aboutMe, searchQuery)}
              </p>
            )}

            {/* Join Date & Credits */}
            <div className="flex items-center gap-4 text-xs text-gray-500">
              {getJoinDate() && (
                <div className="flex items-center gap-1">
                  <Calendar className="h-3 w-3" />
                  <span>Joined {getJoinDate()}</span>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
} 